<template>
  <div class="sp-tab" :class="classModifiers" part="base" role="none">
    <sp-button ref="button" variant="text" color="primary" part="activator">
      <slot />
    </sp-button>
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, shallowRef, useTemplateRef, watch } from "vue";

import { useExpose } from "#ce/composables/useExpose";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

defineOptions({ inheritAttrs: false });

const emit = defineEmits(["update-active"]);

const props = defineProps({
  panel: {
    type: String,
    required: true,
  },
  active: booleanAttribute(),
});

const navigationActive = shallowRef(null);
const isActive = computed(() => navigationActive.value ?? toBoolean(props.active));
const tabindex = shallowRef(-1);
let panelElement = null;
const button = useTemplateRef("button");
const supportsElementReflection = "ariaControlsElements" in Element.prototype;

function syncTabControl() {
  const control = button.value?.controlElement;
  if (!control) {
    return;
  }

  control.setAttribute("role", "tab");
  control.setAttribute("aria-selected", String(isActive.value));
  control.tabIndex = tabindex.value;

  if (supportsElementReflection) {
    control.ariaControlsElements = panelElement ? [panelElement] : null;
  }
}

watch([button, isActive, tabindex], syncTabControl, { immediate: true, flush: "post" });
useEventListener(button, "sp:ce:ready", syncTabControl);

const { exposeMethods } = useExpose();
exposeMethods({
  focus: (options) => button.value?.focus(options),
  setNavigationState(state) {
    navigationActive.value = state.active;
    tabindex.value = state.tabindex;
    panelElement = state.panel;
    syncTabControl();
  },
});

const classModifiers = computed(() => ({
  "--is-active": isActive.value,
}));

watch(isActive, (value) => {
  emit("update-active", value);
});
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-tab {
  --sp-comp-button-border-radius: var(--sp-comp-tab-button-border-radius, 0);
  --sp-comp-button-text-color: var(--sp-comp-tab-button-text-color, #aaa);
  --sp-comp-button-default-hover-text-color: var(--sp-sys-color-primary);
}
</style>
